import { useState } from 'react';
import { Modal, Select, Input, message } from 'antd';
import type { UploadFile } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClaimEligibility, useFileClaim } from '@/hooksApi/useClaim';
import { getEligibilityReasonMessage, formatClaimDeadline, formatIQD } from '@/utils/claimHelpers';
import { ClaimType } from '@/types';
import EvidenceUpload from './EvidenceUpload';

interface FileClaimModalProps {
  orderUid: string;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ClaimTypeOption = { label: string; value: ClaimType };

const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 1000;

const FileClaimModal = ({ orderUid, visible, onClose, onSuccess }: FileClaimModalProps) => {
  const { t } = useTranslation();

  const [claimType, setClaimType] = useState<ClaimType | null>(null);
  const [description, setDescription] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [existingClaimUid, setExistingClaimUid] = useState<string | null>(null);

  const { data: eligibilityData, isLoading: isCheckingEligibility } = useClaimEligibility(
    orderUid,
    visible,
  );

  const { mutateAsync: fileClaim, isLoading: isSubmitting } = useFileClaim();

  const eligibility = eligibilityData?.object ?? null;

  // Path A (parcel never reached a carrier; backend sets
  // presumed_responsible_party='boxy'). UX intent: merchants see ZERO visible
  // difference vs the carrier claim flow — same form, same type options
  // (damaged + partially_damaged), no banner, no hints, and the same
  // mandatory evidence requirement. Path A behaviour lives entirely in
  // eligibility + downstream settlement.

  // BOXY-501 — a LOST parcel/box has nothing to photograph: evidence optional.
  const isFormValid =
    claimType !== null &&
    description.trim().length >= DESCRIPTION_MIN &&
    (claimType === 'lost' || fileList.length >= 1);

  const resetForm = () => {
    setClaimType(null);
    setDescription('');
    setFileList([]);
    setExistingClaimUid(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid || !claimType) return;

    const formData = new FormData();
    formData.append('type', claimType);
    formData.append('description', description.trim());
    fileList.forEach((f, index) => {
      if (f.originFileObj) {
        formData.append(`evidence[${index}]`, f.originFileObj);
      }
    });

    try {
      await fileClaim({ orderUid, formData });
      message.success(
        String(t('claim_filed_successfully', { defaultValue: 'Claim filed successfully.' })),
      );
      resetForm();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { object?: { uid?: string } } } };
      if (error?.response?.status === 409) {
        const uid = error?.response?.data?.object?.uid ?? null;
        setExistingClaimUid(uid);
      }
    }
  };

  // Same options for Path A and carrier. BOXY-501 (owner 10-07): merchants
  // can file `lost` too — e.g. a box that never came back after a partial delivery.
  const claimTypeOptions: ClaimTypeOption[] = [
    {
      label: String(t('claim_type.damaged', { defaultValue: 'Damaged' })),
      value: 'damaged',
    },
    {
      label: String(t('claim_type.partially_damaged', { defaultValue: 'Partially Damaged' })),
      value: 'partially_damaged',
    },
    {
      label: String(t('claim_type.lost', { defaultValue: 'Lost' })),
      value: 'lost',
    },
  ];

  const renderBody = () => {
    if (isCheckingEligibility) {
      return (
        <div className='flex items-center justify-center py-10'>
          <div className='h-8 w-8 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
        </div>
      );
    }

    if (!eligibility) {
      return (
        <span className='text-sm text-[rgb(115,123,140)]'>
          {String(
            t('claim_eligibility_loading_failed', {
              defaultValue: 'Could not load eligibility information.',
            }),
          )}
        </span>
      );
    }

    if (!eligibility.eligible) {
      const reasonMessage = getEligibilityReasonMessage(
        eligibility.reason,
        t,
        eligibility.claim_window_hours,
      );
      return (
        <div className='flex flex-col gap-3 py-2'>
          <span className='text-sm text-[rgb(239,68,68)]'>{reasonMessage}</span>
          {existingClaimUid && (
            <Link
              to={`/claims/${existingClaimUid}`}
              onClick={handleClose}
              className='text-sm text-[rgb(57,57,60)] underline hover:opacity-70'
            >
              {String(t('view_existing_claim', { defaultValue: 'View existing claim' }))}
            </Link>
          )}
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-3 py-1'>
        {/* Declared value + deadline info */}
        <div className='rounded-2xl border border-[rgb(232,230,227)] bg-[rgb(249,250,251)] p-4'>
          <div className='flex justify-between flex-wrap gap-2'>
            <div className='flex flex-col gap-0.5'>
              <span className='text-xs text-[rgb(115,123,140)]'>
                {String(t('declared_value', { defaultValue: 'Declared Value' }))}
              </span>
              <span className='text-sm font-semibold text-[rgb(57,57,60)]'>
                {formatIQD(eligibility.order_declared_value)}
              </span>
            </div>
            <div className='flex flex-col gap-0.5'>
              <span className='text-xs text-[rgb(115,123,140)]'>
                {String(t('claim_deadline', { defaultValue: 'Claim Deadline' }))}
              </span>
              <span className='text-sm font-semibold text-[rgb(57,57,60)]'>
                {formatClaimDeadline(eligibility.deadline, t)}
              </span>
            </div>
          </div>
        </div>

        {/* Claim type */}
        <div className='flex flex-col gap-1'>
          <span className='text-sm font-semibold text-[rgb(57,57,60)]'>
            {String(t('claim_type_label', { defaultValue: 'Claim Type' }))}
          </span>
          <Select<ClaimType>
            placeholder={String(t('select_claim_type', { defaultValue: 'Select claim type' }))}
            options={claimTypeOptions}
            value={claimType ?? undefined}
            onChange={(val) => setClaimType(val)}
            className='w-full'
          />
        </div>

        {/* Description */}
        <div className='flex flex-col gap-1'>
          <span className='text-sm font-semibold text-[rgb(57,57,60)]'>
            {String(t('description', { defaultValue: 'Description' }))}
          </span>
          <Input.TextArea
            placeholder={String(
              t('claim_description_placeholder', {
                defaultValue: 'Describe the issue in detail...',
              }),
            )}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={DESCRIPTION_MAX}
            showCount
            rows={3}
          />
          {description.length > 0 && description.trim().length < DESCRIPTION_MIN && (
            <span className='text-xs text-[rgb(239,68,68)]'>
              {String(
                t('claim_description_too_short', {
                  defaultValue: `Minimum ${DESCRIPTION_MIN} characters required.`,
                  min: DESCRIPTION_MIN,
                }),
              )}
            </span>
          )}
        </div>

        {/* Evidence upload — silently optional for Path A (backend allows it).
            We deliberately do NOT label this differently — the merchant sees
            the same field as the carrier flow. */}
        <div className='flex flex-col gap-1'>
          <span className='text-sm font-semibold text-[rgb(57,57,60)]'>
            {String(t('evidence', { defaultValue: 'Evidence' }))}
          </span>
          <EvidenceUpload fileList={fileList} onChange={(info) => setFileList(info.fileList)} />
        </div>

        {/* 409 conflict error */}
        {existingClaimUid && (
          <div className='flex items-center gap-2'>
            <span className='text-sm text-[rgb(239,68,68)]'>
              {String(
                t('claim_already_exists_error', {
                  defaultValue: 'A claim already exists for this order.',
                }),
              )}
            </span>
            <Link
              to={`/claims/${existingClaimUid}`}
              onClick={handleClose}
              className='text-sm text-[rgb(57,57,60)] underline hover:opacity-70'
            >
              {String(t('view_claim', { defaultValue: 'View claim' }))}
            </Link>
          </div>
        )}
      </div>
    );
  };

  const showFooter = eligibility?.eligible === true && !isCheckingEligibility;

  return (
    <Modal
      title={String(t('file_a_claim', { defaultValue: 'File a Claim' }))}
      open={visible}
      onCancel={handleClose}
      footer={
        showFooter
          ? [
              <button
                key='cancel'
                onClick={handleClose}
                className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
              >
                {String(t('cancel', { defaultValue: 'Cancel' }))}
              </button>,
              <button
                key='submit'
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
              >
                {isSubmitting ? (
                  <span className='flex items-center gap-1.5'>
                    <span className='h-4 w-4 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                    {String(t('submit_claim', { defaultValue: 'Submit Claim' }))}
                  </span>
                ) : (
                  String(t('submit_claim', { defaultValue: 'Submit Claim' }))
                )}
              </button>,
            ]
          : null
      }
      destroyOnClose
      width={520}
      centered
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {renderBody()}
    </Modal>
  );
};

export default FileClaimModal;
